import React from 'react';

import type { CannedFieldElement } from '@aline/common';
import { CANNED_FIELD_METADATA } from '@aline/common';
import { link, params } from '@aline/routes';
import { IconButton, Paper, Typography } from '@mui/material';
import { Copy, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { NodeEntry } from 'slate';
import {
  AlineTooltip,
  defaultEditorPopoverPaperStyles,
  setVariablePanelFocusedField,
  Spacer,
} from 'src/components';
import { CANNED_ICON_BY_VARIANT } from 'src/components/icons/CannedFieldIcons';
import { IconCircle } from 'src/components/icons/IconCircle';
import { useRequiredEditorRuntime } from 'src/editor/editor-runtime-registry';
import { getPathWithDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { ClientTransforms } from 'src/editor/transforms';
import { useDispatchSuccessSnack, useDocumentParams } from 'src/helpers';
import { useDocumentSignatureStatus, useGetCurrentDoc, useWorkflowCannedFields } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { makeStyles } from 'tss-react/mui';

interface CannedFieldPopoverContentProps {
  cannedFieldEntry: NodeEntry<CannedFieldElement>;
}

const useStyles = makeStyles()(theme => ({
  buttons: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  helperText: {
    color: theme.appColors.secondaryType,
  },
  icon: {
    height: 18,
    width: 18,
  },
  labelColumn: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  section: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  spacer: {
    minWidth: theme.spacing(2),
  },
  wrapper: {
    alignItems: 'center',
    borderRadius: 3,
    display: 'flex',
    padding: theme.spacing(1, 1.5),
  },
}));

export function CannedFieldPopoverContent({
  cannedFieldEntry: [cannedElement, cannedPath],
}: CannedFieldPopoverContentProps): JSX.Element {
  const { classes } = useStyles();
  const editor = useEditor();
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();
  const { tabId } = useRequiredEditorRuntime();
  const { openDrawer } = useGlobalDrawer();
  const meta = CANNED_FIELD_METADATA[cannedElement.variant];
  const dispatchFieldCopied = useDispatchSuccessSnack({ message: 'Field copied to clipboard!' });
  const Icon = CANNED_ICON_BY_VARIANT[cannedElement.variant];
  const { document } = useGetCurrentDoc(documentId);
  const { cannedById } = useWorkflowCannedFields(document?.workflow?.id ?? null);
  const cannedField = cannedById.get(cannedElement.cannedFieldId);
  const { wasSentForSignature } = useDocumentSignatureStatus(documentId);
  const helperText = wasSentForSignature
    ? meta.buildTooltip(cannedField?.value ?? cannedField?.defaultValue ?? null)
    : meta.authorHelpText;

  function focusInOutline(): void {
    navigate(
      getPathWithDrawerSearchParam(
        link.DocumentDetailFields({ documentId }),
        window.location.search,
        params.rightPanel.editFieldId,
        null
      ),
      { replace: true }
    );
    openDrawer();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setVariablePanelFocusedField(tabId, {
          __typename: 'CannedField',
          id: cannedElement.cannedFieldId,
          nodeId: cannedElement.cannedFieldId,
        });
      });
    });
  }

  return (
    <Paper style={defaultEditorPopoverPaperStyles}>
      <div className={classes.wrapper}>
        <div className={classes.section}>
          <IconCircle icon={<Icon className={classes.icon} />} />
          <div className={classes.labelColumn}>
            <Typography noWrap>{`${meta.label} (auto-populated field)`}</Typography>
            <Typography noWrap className={classes.helperText} variant="caption">
              {helperText}
            </Typography>
          </div>
        </div>
        <Spacer className={classes.spacer} />
        <div className={classes.buttons}>
          <AlineTooltip arrow placement="top" title="View in outline">
            <div>
              <IconButton size="small" onClick={focusInOutline}>
                <Eye className={classes.icon} />
              </IconButton>
            </div>
          </AlineTooltip>
          <AlineTooltip arrow placement="top" title="Copy field to clipboard">
            <div>
              <IconButton
                size="small"
                onClick={async () => {
                  const isCopied = await ClientTransforms.copyToClipboard(editor, {
                    at: cannedPath,
                    documentId,
                  });
                  if (isCopied) dispatchFieldCopied();
                }}
              >
                <Copy className={classes.icon} />
              </IconButton>
            </div>
          </AlineTooltip>
        </div>
      </div>
    </Paper>
  );
}
